import { supabase as supabaseClient } from './supabase';
import { StorageLimitService } from './storage-limit-service';

export interface UserLimits {
  monthlyCredits: number;
  teamSeatsLimit: number;
  projectsLimit: number; 
  versionsLimit: number;
  planName: string;
  planId: string;
  maxStorageBytes: number;
}

export interface UsageCounts {
  currentAnalysesUsed: number;
  currentTeamSeatsUsed: number;
  currentProjectsCount: number;
  currentVersionsUsed: number;
  totalCreditsAvailable: number;
  currentStorageUsed: number;
}

export interface LimitCheckResult {
  allowed: boolean;
  reason?: string;
  upgradeRequired?: boolean;
  suggestedPlan?: string;
  currentUsage?: number;
  limit?: number;
}

export interface SubscriptionLimits {
  monthlyCredits: number;
  teamSeatsLimit: number;
  projectsLimit: number;
  versionsLimit: number;
  maxStorageBytes: number;
}

export interface UsageSummary extends SubscriptionLimits, UsageCounts {
  planName: string;
  creditsRemaining: number;
  teamSeatsRemaining: number;
  projectsRemaining: number;
  versionsRemaining: number;
  storageRemaining: number;
  storagePercentageUsed: number;
  isOverLimit: boolean;
  upgradeRecommendations: string[];
}

/**
 * Free trial limits used as fallback when no subscription is found
 */
const FREE_TRIAL_LIMITS: SubscriptionLimits = {
  monthlyCredits: 3,
  teamSeatsLimit: 1,
  projectsLimit: 1,
  versionsLimit: 2,
  maxStorageBytes: 1073741824 // 1 GB
};

export class LimitEnforcementService {
  /**
   * Get user's current subscription limits and plan details
   */
  static async getUserLimits(userId: string): Promise<UserLimits | null> {
    try {
      // Check if user is a team member and get organization owner if so
      const { data: userData } = await supabaseClient
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();

      let targetUserId = userId;
      if (userData?.organization_id && userData?.team_role) {
        // Check if this user is a team member (not organization owner)
        const { data: orgData } = await supabaseClient
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== userId) {
          // This is a team member - use organization owner's subscription
          targetUserId = orgData.created_by;
          console.log('🔄 Team member detected - getting subscription limits for organization owner:', {
            teamMember: userId,
            organizationOwner: targetUserId
          });
        }
      }

      // Get the target user's most recent subscription (team-aware using RPC to bypass RLS)
      let subscription = null;
      let subError = null;
      
      if (targetUserId !== userId) {
        // Use RPC function to get organization owner's subscription (bypasses RLS)
        const { data: subscriptionData, error: rpcError } = await supabaseClient
          .rpc('get_team_aware_subscription_context', { target_user_id: targetUserId });
        
        if (!rpcError && subscriptionData && subscriptionData.length > 0) {
          subscription = subscriptionData[0];
        } else {
          subError = rpcError;
        }
      } else {
        // Regular query for the user's own subscription
        const { data: subData, error: regularError } = await supabaseClient
          .from('user_subscriptions')
          .select('plan_id, status, created_at, current_period_end')
          .eq('user_id', targetUserId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        subscription = subData;
        subError = regularError;
      }

      if (subError) {
        console.error('❌ Error fetching subscription:', subError);
        console.log('🆓 Falling back to free trial limits due to error');
        return {
          monthlyCredits: 3,
          teamSeatsLimit: 1,
          projectsLimit: 1,
          versionsLimit: 2,
          planName: 'Free Trial',
          planId: 'trial',
          maxStorageBytes: 1073741824 // 1 GB
        };
      }

      if (!subscription) {
        console.log('🆓 No subscription found, using free trial limits');
        return {
          monthlyCredits: 3,
          teamSeatsLimit: 1,
          projectsLimit: 1,
          versionsLimit: 2,
          planName: 'Free Trial',
          planId: 'trial',
          maxStorageBytes: 1073741824 // 1 GB
        };
      }

      // Check if subscription is still valid (active or canceled but within period)
      const now = new Date();
      const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : new Date(0);
      const isWithinPeriod = periodEnd > now;
      
      // If subscription is expired, use free trial limits
      if (!isWithinPeriod && subscription.status === 'canceled') {
        console.log('🆓 Subscription expired, using free trial limits');
        return {
          monthlyCredits: 3,
          teamSeatsLimit: 1,
          projectsLimit: 1,
          versionsLimit: 2,
          planName: 'Free Trial',
          planId: 'trial',
          maxStorageBytes: 1073741824 // 1 GB
        };
      }

      // Now get the plan details
      const { data: plan, error: planError } = await supabaseClient
        .from('subscription_plans')
        .select('name, monthly_credits, team_seats_limit, projects_limit, versions_limit, max_storage_bytes')
        .eq('id', subscription.plan_id)
        .maybeSingle();

      if (planError || !plan) {
        console.error('❌ Error fetching plan details:', planError);
        console.log('🆓 Falling back to free trial limits due to plan error');
        return {
          monthlyCredits: 3,
          teamSeatsLimit: 1,
          projectsLimit: 1,
          versionsLimit: 2,
          planName: 'Free Trial',
          planId: 'trial',
          maxStorageBytes: 1073741824 // 1 GB
        };
      }


      return {
        monthlyCredits: plan.monthly_credits ?? 0,
        teamSeatsLimit: plan.team_seats_limit === -1 ? Infinity : (plan.team_seats_limit ?? 1),
        projectsLimit: plan.projects_limit === -1 ? Infinity : (plan.projects_limit ?? 1),
        versionsLimit: plan.versions_limit === -1 ? Infinity : (plan.versions_limit ?? 2),
        planName: plan.name ?? 'Unknown Plan',
        planId: subscription.plan_id,
        maxStorageBytes: plan.max_storage_bytes ?? 1073741824 // Default to 1 GB
      };
    } catch (error) {
      console.error('❌ Unexpected error in getUserLimits:', error);
      console.log('🆓 Falling back to free trial limits due to error');
      return {
        monthlyCredits: 3,
        teamSeatsLimit: 1,
        projectsLimit: 1,
        versionsLimit: 2,
        creditRolloverPercentage: 0,
        planName: 'Free Trial',
        planId: 'trial',
        maxStorageBytes: 1073741824 // 1 GB
      };
    }
  }

  /**
   * Get user's current usage counts
   */
  static async getUsageCounts(userId: string): Promise<UsageCounts | null> {
    try {
      // Check if user is a team member and get organization owner if so
      const { data: userData } = await supabaseClient
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();

      let targetUserId = userId;
      if (userData?.organization_id && userData?.team_role) {
        // Check if this user is a team member (not organization owner)
        const { data: orgData } = await supabaseClient
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== userId) {
          // This is a team member - use organization owner's limits
          targetUserId = orgData.created_by;
          console.log('🔄 Team member detected - getting usage counts for organization owner:', {
            teamMember: userId,
            organizationOwner: targetUserId
          });
        }
      }

      // Get subscription limits using team-aware RPC function
      const { data: limitsData, error: limitsError } = await supabaseClient
        .rpc('get_team_aware_subscription_limits', { target_user_id: targetUserId });
      
      const limits = limitsData && limitsData.length > 0 ? limitsData[0] : null;

      if (limitsError) {
        console.error('❌ Error fetching usage limits:', limitsError);
        // Initialize default limits if missing
        await this.initializeUserLimits(userId);
        return {
          currentAnalysesUsed: 0,
          currentTeamSeatsUsed: 0,
          currentProjectsCount: 0,
          currentVersionsUsed: 0,
          totalCreditsAvailable: 3, // Default free trial
          currentStorageUsed: 0
        };
      }

      if (!limits) {
        console.log('📝 No limits record found, initializing...');
        await this.initializeUserLimits(userId);
        return {
          currentAnalysesUsed: 0,
          currentTeamSeatsUsed: 0,
          currentProjectsCount: 0,
          currentVersionsUsed: 0,
          totalCreditsAvailable: 3, // Default free trial
          currentStorageUsed: 0
        };
      }


      // Get monthly credits from subscription limits
      const userLimits = await this.getUserLimits(userId);
      const monthlyCredits = userLimits?.monthlyCredits || 0;
      const totalCreditsAvailable = monthlyCredits;

      // Get real storage usage from StorageLimitService
      let currentStorageUsed = 0;
      try {
        currentStorageUsed = await StorageLimitService.calculateUserStorageUsage(userId);
        // Update the stored value for future use
        await StorageLimitService.updateStorageUsage(userId, currentStorageUsed);
      } catch (error) {
        console.error('❌ Error calculating real storage usage:', error);
        currentStorageUsed = limits.current_storage_used ?? 0;
      }

      // Get real project count
      let currentProjectsCount = 0;
      try {
        const { data: projects, error: projectError } = await supabaseClient
          .from('projects')
          .select('id')
          .eq('user_id', targetUserId); // Use targetUserId instead of userId for team awareness
        
        if (!projectError) {
          currentProjectsCount = projects?.length || 0;
          // Update the stored value
          await supabaseClient
            .from('subscription_limits')
            .update({ current_projects_count: currentProjectsCount })
            .eq('user_id', targetUserId); // Use targetUserId instead of userId
        } else {
          console.error('❌ Error getting real project count:', projectError);
          currentProjectsCount = limits.current_projects_count ?? 0;
        }
      } catch (error) {
        console.error('❌ Error calculating real project count:', error);
        currentProjectsCount = limits.current_projects_count ?? 0;
      }

      // Get real analysis count (from creatives with analysis_data) - billing period aware
      let currentAnalysesUsed = 0;
      try {
        // Get the current billing period start date to filter analyses
        // If no period start is set, use current time (new users) or fall back to stored value
        const periodStart = limits.current_period_start;
        
        if (periodStart) {
          // Only recalculate if we have a valid billing period start
          const { data: analyzedCreatives, error: analysisError } = await supabaseClient
            .from('creatives')
            .select('id, created_at')
            .eq('user_id', targetUserId) // Use targetUserId instead of userId for team awareness
            .not('analysis_data', 'is', null)
            .gte('created_at', periodStart); // Only count analyses from current billing period
          
          if (!analysisError) {
            currentAnalysesUsed = analyzedCreatives?.length || 0;
            console.log(`📊 Billing-period aware analysis count: ${currentAnalysesUsed} (since ${periodStart}) for user ${targetUserId}`);
            
            // Update the stored value only if it differs significantly from stored value
            const storedValue = limits.current_analyses_used ?? 0;
            if (currentAnalysesUsed !== storedValue) {
              // Check if a manual update was made recently (within last 30 seconds)
              // This prevents auto-recalculation from overriding API credit deductions
              const lastUpdate = new Date(limits.updated_at || '1970-01-01');
              const timeSinceUpdate = Date.now() - lastUpdate.getTime();
              
              if (timeSinceUpdate < 30000) { // 30 seconds
                console.log(`⏸️ Skipping auto-recalculation - manual update was ${Math.round(timeSinceUpdate/1000)}s ago`);
                currentAnalysesUsed = storedValue; // Use the manually updated value
              } else {
                console.log(`🔄 Updating analysis count from ${storedValue} to ${currentAnalysesUsed} (billing-period aware) for user ${targetUserId}`);
                await supabaseClient
                  .from('subscription_limits')
                  .update({ current_analyses_used: currentAnalysesUsed })
                  .eq('user_id', targetUserId); // Use targetUserId instead of userId
              }
            }
          } else {
            console.error('❌ Error getting real analysis count:', analysisError);
            currentAnalysesUsed = limits.current_analyses_used ?? 0;
          }
        } else {
          // No billing period set - use stored value and don't recalculate
          console.log('⚠️ No billing period start set, using stored analysis count');
          currentAnalysesUsed = limits.current_analyses_used ?? 0;
        }
      } catch (error) {
        console.error('❌ Error calculating real analysis count:', error);
        currentAnalysesUsed = limits.current_analyses_used ?? 0;
      }

      // Get real team seats count (from organization membership)
      let currentTeamSeatsUsed = 1; // Default to 1 (the user themselves)
      try {
        // First get the target user's organization
        const { data: targetUserData, error: targetUserError } = await supabaseClient
          .from('users')
          .select('organization_id')
          .eq('id', targetUserId)
          .single();
        
        if (!targetUserError && targetUserData?.organization_id) {
          // Count all users in the same organization
          const { data: teamMembers, error: teamError } = await supabaseClient
            .from('users')
            .select('id')
            .eq('organization_id', targetUserData.organization_id);
          
          if (!teamError) {
            currentTeamSeatsUsed = teamMembers?.length || 1;
            // Update the stored value for the target user
            await supabaseClient
              .from('subscription_limits')
              .update({ current_team_seats_used: currentTeamSeatsUsed })
              .eq('user_id', targetUserId);
          } else {
            console.error('❌ Error getting real team count:', teamError);
            currentTeamSeatsUsed = limits.current_team_seats_used ?? 1;
          }
        } else {
          // Target user doesn't have an organization (individual user) - this is normal
          currentTeamSeatsUsed = limits.current_team_seats_used ?? 1;
        }
      } catch (error) {
        console.error('❌ Error calculating real team seats count:', error);
        currentTeamSeatsUsed = limits.current_team_seats_used ?? 1;
      }

      return {
        currentAnalysesUsed,
        currentTeamSeatsUsed,
        currentProjectsCount,
        currentVersionsUsed: limits.current_versions_used ?? 0,
        totalCreditsAvailable,
        currentStorageUsed
      };
    } catch (error) {
      console.error('❌ Unexpected error in getUsageCounts:', error);
      return null;
    }
  }

  /**
   * Initialize subscription limits tracking for a user
   */
  static async initializeUserLimits(userId: string): Promise<void> {
    try {
      console.log('🛠️ Initializing user limits for:', userId);
      
      // Check if user is a team member - if so, don't create their own subscription_limits
      const { data: userData } = await supabaseClient
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();
      
      if (userData?.organization_id && userData?.team_role) {
        // Check if this user is the organization owner or a team member
        const { data: orgData } = await supabaseClient
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== userId) {
          console.log('👥 User is a team member - skipping subscription_limits initialization (will use organization owner)');
          return;
        }
      }
      
      const { error } = await supabaseClient
        .from('subscription_limits')
        .upsert({
          user_id: userId,
          current_analyses_used: 0,
          current_team_seats_used: 1, // User themselves counts as 1 seat
          current_projects_count: 0,
          current_versions_used: 0,
          current_period_start: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('❌ Error initializing user limits:', error);
      } else {
        console.log('✅ User limits initialized successfully');
      }
    } catch (error) {
      console.error('❌ Unexpected error initializing user limits:', error);
    }
  }

  /**
   * Check if user can perform a creative analysis using subscription-based credit system
   */
  static async canPerformAnalysis(userId: string): Promise<LimitCheckResult> {
    try {
      // Use UnifiedCreditService for consistent credit calculations
      const { UnifiedCreditService } = await import('./unified-credit-service');
      const creditData = await UnifiedCreditService.getCreditData(userId);

      const cost = 1; // Analysis cost is always 1 credit
      const availableCredits = creditData.availableCredits;

      if (availableCredits >= cost) {
        return {
          allowed: true,
          currentUsage: creditData.usedCredits,
          limit: creditData.totalCredits
        };
      }

      const limits = await this.getUserLimits(userId);
      return {
        allowed: false,
        reason: `Insufficient credits. You need ${cost} credit but only have ${availableCredits}.`,
        upgradeRequired: true,
        suggestedPlan: limits?.planId === 'trial' ? 'starter' : 'pro',
        currentUsage: availableCredits,
        limit: cost
      };
    } catch (error) {
      console.error('❌ Error checking analysis capability:', error);
      return {
        allowed: false,
        reason: 'Unable to verify analysis capability. Please try again.',
        upgradeRequired: false
      };
    }
  }

  /**
   * Check if user can add team members
   */
  static async canAddTeamMember(userId: string): Promise<LimitCheckResult> {
    const limits = await this.getUserLimits(userId);
    const usage = await this.getUsageCounts(userId);

    if (!limits || !usage) {
      return { allowed: false, reason: 'Unable to verify subscription limits' };
    }

    if (usage.currentTeamSeatsUsed >= limits.teamSeatsLimit) {
      return {
        allowed: false,
        reason: `Team member limit reached (${limits.teamSeatsLimit}). Upgrade to add more team members.`,
        upgradeRequired: true,
        suggestedPlan: limits.planId === 'trial' ? 'starter' : 'pro',
        currentUsage: usage.currentTeamSeatsUsed,
        limit: limits.teamSeatsLimit
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can create projects
   */
  static async canCreateProject(userId: string): Promise<LimitCheckResult> {
    const limits = await this.getUserLimits(userId);
    const usage = await this.getUsageCounts(userId);

    if (!limits || !usage) {
      return { allowed: false, reason: 'Unable to verify subscription limits' };
    }

    if (usage.currentProjectsCount >= limits.projectsLimit) {
      return {
        allowed: false,
        reason: `Project limit reached (${limits.projectsLimit}). Upgrade to create more projects.`,
        upgradeRequired: true,
        suggestedPlan: limits.planId === 'trial' ? 'starter' : 'pro',
        currentUsage: usage.currentProjectsCount,
        limit: limits.projectsLimit
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can create a version for a specific creative
   */
  static async canCreateVersion(userId: string, creativeId: string): Promise<LimitCheckResult> {
    const limits = await this.getUserLimits(userId);

    if (!limits) {
      return { allowed: false, reason: 'Unable to verify subscription limits' };
    }

    if (limits.versionsLimit === Infinity || limits.versionsLimit === -1) {
      return { allowed: true }; // Unlimited versions for higher plans
    }

    // Get the number of versions for this specific creative
    const rootCreativeId = await this.getRootCreativeId(creativeId);
    const currentVersionCount = await this.getVersionCountForCreative(rootCreativeId);

    if (currentVersionCount >= limits.versionsLimit) {
      return {
        allowed: false,
        reason: `This creative has reached the version limit (${currentVersionCount}/${limits.versionsLimit} versions). Upgrade to create more versions per creative.`,
        upgradeRequired: true,
        suggestedPlan: limits.planId === 'trial' ? 'starter' : 'agency',
        currentUsage: currentVersionCount,
        limit: limits.versionsLimit
      };
    }

    return { 
      allowed: true,
      currentUsage: currentVersionCount,
      limit: limits.versionsLimit
    };
  }

  /**
   * Check if user can upload a file based on storage limits
   */
  static async canUploadFile(userId: string, fileSizeBytes: number): Promise<LimitCheckResult> {
    try {
      // Use StorageLimitService for detailed storage check
      const storageCheck = await StorageLimitService.canUploadFile(userId, fileSizeBytes);
      
      if (!storageCheck.allowed) {
        return {
          allowed: false,
          reason: storageCheck.reason,
          upgradeRequired: true,
          suggestedPlan: storageCheck.requiredUpgrade,
          currentUsage: storageCheck.currentUsage?.currentUsageBytes,
          limit: storageCheck.currentUsage?.maxStorageBytes
        };
      }
      
      return { allowed: true };
    } catch (error) {
      console.error('Error checking storage limit:', error);
      return { allowed: true }; // Allow upload on error
    }
  }

  /**
   * Helper: Get the root creative ID (handles both parent and child creatives)
   */
  private static async getRootCreativeId(creativeId: string): Promise<string> {
    try {
      const { data: creative, error } = await supabaseClient
        .from('creatives')
        .select('id, parent_creative_id')
        .eq('id', creativeId)
        .maybeSingle();

      if (error || !creative) {
        return creativeId; // Fallback to original ID
      }

      // Return the root ID (parent if exists, otherwise the creative itself)
      return creative.parent_creative_id || creative.id;
    } catch (error) {
      console.error('Error getting root creative ID:', error);
      return creativeId; // Fallback
    }
  }

  /**
   * Helper: Get the number of versions for a specific creative family
   */
  private static async getVersionCountForCreative(rootCreativeId: string): Promise<number> {
    try {
      const { data: versions, error } = await supabaseClient
        .from('creatives')
        .select('id')
        .or(`id.eq.${rootCreativeId},parent_creative_id.eq.${rootCreativeId}`);

      if (error) {
        console.error('Error counting versions for creative:', error);
        return 0;
      }

      return versions?.length || 0;
    } catch (error) {
      console.error('Error counting versions for creative:', error);
      return 0;
    }
  }

  /**
   * Consume credits for an analysis using subscription-based credit system
   */
  static async consumeAnalysisCredit(userId: string, analysisId?: string): Promise<boolean> {
    try {
      // Check if analysis is allowed first
      const canAnalyze = await this.canPerformAnalysis(userId);
      if (!canAnalyze.allowed) {
        return false;
      }

      // Use the new CreditService to deduct credits consistently
      const { CreditService } = await import('./credits');
      const result = await CreditService.deductCreditsForAnalysis(analysisId);

      if (!result.success) {
        console.error('Failed to deduct credits for analysis:', result);
        return false;
      }

      console.log(`✅ Analysis credit consumed. New balance: ${result.newBalance}, Cost: ${result.cost}`);
      return true;
    } catch (error) {
      console.error('Error consuming analysis credit:', error);
      return false;
    }
  }

  /**
   * Check if user can perform creative enhancement
   */
  static async canPerformEnhancement(): Promise<LimitCheckResult> {
    try {
      // Check if user has credits available (1 credit for enhancement)
      const { CreditService } = await import('./credits');
      const canEnhance = await CreditService.canPerformAnalysis(); // Same credit logic as analysis
      
      if (!canEnhance.canAnalyze) {
        return {
          allowed: false,
          reason: canEnhance.message || 'Insufficient credits for enhancement. Upgrade to get more credits.',
          upgradeRequired: true,
          suggestedPlan: 'starter',
          currentUsage: canEnhance.currentBalance,
          limit: canEnhance.cost
        };
      }

      return { allowed: true };
    } catch (error) {
      console.error('Error checking enhancement capability:', error);
      return { allowed: false, reason: 'Unable to verify credit balance' };
    }
  }

  /**
   * Consume credits for creative enhancement using subscription-based credit system
   */
  static async consumeEnhancementCredit(creativeId: string, enhancementId?: string): Promise<boolean> {
    try {
      // Check if enhancement is allowed first
      const canEnhance = await this.canPerformEnhancement();
      if (!canEnhance.allowed) {
        return false;
      }

      // Use the CreditService to deduct credits consistently
      const { CreditService } = await import('./credits');
      const result = await CreditService.deductCreditsForEnhancement(creativeId, enhancementId);

      if (!result.success) {
        console.error('Failed to deduct credits for enhancement:', result);
        return false;
      }

      console.log(`✅ Enhancement credit consumed. New balance: ${result.newBalance}, Cost: ${result.cost}`);
      return true;
    } catch (error) {
      console.error('Error consuming enhancement credit:', error);
      return false;
    }
  }

  /**
   * Increment usage counters (team-aware)
   * Note: Version tracking is now handled per creative, not globally
   */
  static async incrementUsageCounter(
    userId: string, 
    type: 'analyses' | 'team_seats' | 'projects'
  ): Promise<void> {
    try {
      // Check if user is part of an organization (team member)
      const { data: userData, error: userError } = await supabaseClient
        .from('users')
        .select('organization_id, team_role, subscription_plan')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user data for increment:', userError);
        return;
      }

      let targetUserId = userId;

      // If user is a team member, increment the organization owner's usage
      if (userData.organization_id && userData.team_role && userData.subscription_plan === null) {
        console.log('👥 Team member analysis - incrementing organization owner usage');
        
        // Find organization owner
        const { data: orgOwner, error: ownerError } = await supabaseClient
          .from('users')
          .select('id')
          .eq('organization_id', userData.organization_id)
          .eq('team_role', 'admin')
          .not('subscription_plan', 'is', null)
          .limit(1)
          .single();

        if (ownerError || !orgOwner) {
          console.error('Could not find organization owner for credit increment:', ownerError);
          return;
        }

        targetUserId = orgOwner.id;
        console.log('🔄 Incrementing usage for organization owner:', targetUserId, 'instead of team member:', userId);
      }

      // Get current limits for the target user (owner or individual user)
      const { data: limits, error: fetchError } = await supabaseClient
        .from('subscription_limits')
        .select('*')
        .eq('user_id', targetUserId)
        .maybeSingle();

      if (fetchError || !limits) {
        console.error(`Error fetching limits for incrementing ${type}:`, fetchError);
        return;
      }

      // Increment the appropriate field
      const updates: any = {};
      if (type === 'analyses') {
        updates.current_analyses_used = (limits.current_analyses_used || 0) + 1;
        console.log('📊 Incrementing analyses:', {
          from: limits.current_analyses_used || 0,
          to: updates.current_analyses_used,
          targetUser: targetUserId,
          originalUser: userId
        });
      } else if (type === 'team_seats') {
        updates.current_team_seats_used = (limits.current_team_seats_used || 0) + 1;
      } else if (type === 'projects') {
        updates.current_projects_count = (limits.current_projects_count || 0) + 1;
      }
      // Note: 'versions' is no longer tracked globally since it's per creative

      const { error } = await supabaseClient
        .from('subscription_limits')
        .update(updates)
        .eq('user_id', targetUserId);

      if (error) {
        console.error(`Error incrementing ${type} counter:`, error);
      } else {
        console.log(`✅ Successfully incremented ${type} counter for user ${targetUserId}`);
      }
    } catch (error) {
      console.error(`Error incrementing ${type} counter:`, error);
    }
  }

  /**
   * Record credit transaction for audit trail
   */
  static async recordCreditTransaction(
    userId: string,
    amount: number,
    type: 'deduction' | 'addition' | 'purchase' | 'subscription_renewal' | 'referral_bonus' | 'refund',
    reason: string,
    relatedId?: string
  ): Promise<void> {
    try {
      const { error } = await supabaseClient
        .from('credit_transactions')
        .insert({
          user_id: userId,
          amount,
          transaction_type: type,
          reason,
          related_analysis_id: type === 'deduction' ? relatedId : null,
          related_subscription_id: type === 'subscription_renewal' ? relatedId : null,
          related_payment_intent_id: type === 'purchase' ? relatedId : null,
          metadata: { timestamp: new Date().toISOString() }
        });

      if (error) {
        console.error('Error recording credit transaction:', error);
      }
    } catch (error) {
      console.error('Error recording credit transaction:', error);
    }
  }

  /**
   * Get formatted usage summary for UI display
   */
  static async getUsageSummary(userId: string): Promise<UsageSummary> {
    try {
      const [limits, usage] = await Promise.all([
        this.getUserLimits(userId),
        this.getUsageCounts(userId)
      ]);

      if (!limits || !usage) {
        // Return safe defaults if limits or usage are null
        return {
          ...FREE_TRIAL_LIMITS,
          planName: 'Free Trial',
          currentAnalysesUsed: 0,
          currentTeamSeatsUsed: 1,
          currentProjectsCount: 0,
          currentVersionsUsed: 0, // Note: No longer used for limit enforcement
          totalCreditsAvailable: FREE_TRIAL_LIMITS.monthlyCredits,
          currentStorageUsed: 0,
          creditsRemaining: FREE_TRIAL_LIMITS.monthlyCredits,
          teamSeatsRemaining: 0,
          projectsRemaining: 0,
          versionsRemaining: FREE_TRIAL_LIMITS.versionsLimit, // Show limit per creative
          storageRemaining: FREE_TRIAL_LIMITS.maxStorageBytes,
          storagePercentageUsed: 0,
          isOverLimit: false,
          upgradeRecommendations: ['Consider upgrading from Free Trial']
        };
      }

      const totalCreditsAvailable = limits.monthlyCredits;
      const creditsRemaining = Math.max(0, totalCreditsAvailable - usage.currentAnalysesUsed);
      
      const teamSeatsRemaining = limits.teamSeatsLimit === -1 ? 999 : Math.max(0, limits.teamSeatsLimit - usage.currentTeamSeatsUsed);
      const projectsRemaining = limits.projectsLimit === -1 ? 999 : Math.max(0, limits.projectsLimit - usage.currentProjectsCount);
      
      // For versions, we now track per creative, so show the limit per creative instead of remaining
      const versionsRemaining = limits.versionsLimit === -1 ? 999 : limits.versionsLimit;
      
      // Calculate storage usage
      const storageRemaining = Math.max(0, limits.maxStorageBytes - usage.currentStorageUsed);
      const storagePercentageUsed = limits.maxStorageBytes > 0 
        ? Math.round((usage.currentStorageUsed / limits.maxStorageBytes) * 100) 
        : 0;

      const isOverLimit = creditsRemaining === 0 || 
                         (limits.teamSeatsLimit !== -1 && teamSeatsRemaining === 0) ||
                         (limits.projectsLimit !== -1 && projectsRemaining === 0) ||
                         storageRemaining === 0;
                         // Removed version limit check since it's now per creative

      const upgradeRecommendations: string[] = [];
      
      if (creditsRemaining === 0) {
        upgradeRecommendations.push('Upgrade for more analysis credits');
      }
      if (limits.teamSeatsLimit !== -1 && teamSeatsRemaining === 0) {
        upgradeRecommendations.push('Upgrade for more team seats');
      }
      if (limits.projectsLimit !== -1 && projectsRemaining === 0) {
        upgradeRecommendations.push('Upgrade for more projects');
      }
      if (limits.versionsLimit !== -1 && limits.versionsLimit < 10) {
        upgradeRecommendations.push('Upgrade for more versions per creative');
      }
      if (storagePercentageUsed >= 90) {
        upgradeRecommendations.push('Storage nearly full - upgrade for more space');
      } else if (storageRemaining === 0) {
        upgradeRecommendations.push('Storage limit reached - upgrade for more space');
      }

      return {
        ...limits,
        ...usage,
        creditsRemaining,
        teamSeatsRemaining,
        projectsRemaining,
        versionsRemaining,
        storageRemaining,
        storagePercentageUsed,
        isOverLimit,
        upgradeRecommendations
      };

    } catch (error) {
      console.error('❌ Error getting usage summary:', error);
      // Return safe defaults
      return {
        ...FREE_TRIAL_LIMITS,
        planName: 'Free Trial',
        currentAnalysesUsed: 0,
        currentTeamSeatsUsed: 1,
        currentProjectsCount: 0,
        currentVersionsUsed: 0,
        rolloverCreditsAvailable: 0,
        totalCreditsAvailable: FREE_TRIAL_LIMITS.monthlyCredits,
        currentStorageUsed: 0,
        creditsRemaining: FREE_TRIAL_LIMITS.monthlyCredits,
        teamSeatsRemaining: 0,
        projectsRemaining: 0,
        versionsRemaining: 1,
        storageRemaining: FREE_TRIAL_LIMITS.maxStorageBytes,
        storagePercentageUsed: 0,
        isOverLimit: false,
        upgradeRecommendations: ['Consider upgrading from Free Trial']
      };
    }
  }
}

export default LimitEnforcementService; 